import { Container, Graphics, Text } from 'pixi.js';
import { AudioBus } from '../../audio/AudioBus';
import { DefaultRandom, type Random } from '../../core/Random';
import { Easings, tween, type TweenHandle } from '../../core/Tween';
import type { WheelConfig, WheelOutcome, WheelSegment } from './types';

/**
 * Rotating wheel divided into N coloured wedges with a fixed pointer at the
 * top. Lifted-in-spirit from cs-core's
 * {@code DisplayWheelOfFortuneAnimator} pattern: wheel rotates with
 * easeOutQuart deceleration, lands such that the chosen segment sits under
 * the pointer.
 *
 * <p>The outcome is decided up-front by weighted random selection (host can
 * inject a {@link Random} for tests). The animation is purely cosmetic —
 * runs to a target rotation that visually places the pre-chosen segment
 * under the pointer, with several extra rotations for kinetic feel.
 */
export class WheelOfFortune extends Container {
  private readonly wheel: Container;
  private readonly pointer: Graphics;
  private readonly random: Random;
  private spinHandle: TweenHandle | null = null;
  private isSpinning = false;
  private currentRotation = 0;

  constructor(public readonly config: WheelConfig, randomImpl?: Random) {
    super();
    this.random = randomImpl ?? new DefaultRandom();

    if (config.segments.length < 2) {
      throw new Error('WheelOfFortune needs at least 2 segments');
    }

    this.wheel = new Container();
    this.addChild(this.wheel);
    this.drawWheel();

    this.pointer = new Graphics();
    this.drawPointer();
    this.addChild(this.pointer);
  }

  /** Outer width = inner radius * 2 + a little for the pointer overshoot. */
  totalWidth(): number {
    return this.config.radius * 2 + 24;
  }

  totalHeight(): number {
    return this.config.radius * 2 + 24;
  }

  /**
   * Spin the wheel. Resolves with the segment the pointer lands on.
   * No-op (returns the current resting outcome) if a spin is already in flight.
   */
  async spin(): Promise<WheelOutcome> {
    if (this.isSpinning) {
      return Promise.reject(new Error('Wheel is already spinning'));
    }
    this.isSpinning = true;

    const targetIndex = this.weightedPickIndex();
    const targetSegment = this.config.segments[targetIndex]!;

    const segmentCount = this.config.segments.length;
    const segmentAngle = (Math.PI * 2) / segmentCount;
    // Wheel angle 0 means segment 0 is centred at the top (under the pointer).
    // To put segment[targetIndex] under the pointer, wheel rotation must equal
    // -targetIndex * segmentAngle (mod 2π). We add several full rotations for
    // kinetic effect, plus a tiny random offset within the segment so two
    // identical landings don't look identical.
    const rotations = this.config.spinRotations ?? 4;
    const wedgeJitter = (this.random.nextFloat() - 0.5) * segmentAngle * 0.6;
    const targetRotation =
      this.currentRotation +
      rotations * Math.PI * 2 -
      ((this.normaliseRotation(this.currentRotation) +
        targetIndex * segmentAngle +
        wedgeJitter) %
        (Math.PI * 2));

    const startRotation = this.currentRotation;
    const totalDelta = targetRotation - startRotation;
    const durationMs = this.config.spinDurationMs ?? 3500;

    let lastSliceIndex = WheelOfFortune.segmentUnderPointer(
      startRotation,
      this.config.segments,
    );
    return new Promise<WheelOutcome>((resolve) => {
      this.spinHandle = tween({
        durationMs,
        easing: Easings.easeOutQuart,
        onUpdate: (p) => {
          const rotation = startRotation + totalDelta * p;
          this.wheel.rotation = rotation;
          const sliceIndex = WheelOfFortune.segmentUnderPointer(
            rotation,
            this.config.segments,
          );
          if (sliceIndex !== lastSliceIndex) {
            AudioBus.shared.play('wheel:tick');
            lastSliceIndex = sliceIndex;
          }
        },
        onComplete: () => {
          this.currentRotation = targetRotation;
          this.wheel.rotation = targetRotation;
          this.isSpinning = false;
          this.spinHandle = null;
          AudioBus.shared.play('wheel:land');
          resolve({ index: targetIndex, segment: targetSegment });
        },
      });
    });
  }

  destroyWheel(): void {
    this.spinHandle?.cancel();
    this.spinHandle = null;
  }

  private weightedPickIndex(): number {
    const totalWeight = this.config.segments.reduce(
      (sum, seg) => sum + (seg.weight ?? 1),
      0,
    );
    let pick = this.random.nextFloat() * totalWeight;
    for (let i = 0; i < this.config.segments.length; i++) {
      pick -= this.config.segments[i]!.weight ?? 1;
      if (pick <= 0) {
        return i;
      }
    }
    return this.config.segments.length - 1;
  }

  private drawWheel(): void {
    const segmentCount = this.config.segments.length;
    const segmentAngle = (Math.PI * 2) / segmentCount;
    const radius = this.config.radius;

    for (let i = 0; i < segmentCount; i++) {
      const segment = this.config.segments[i]!;
      // Segment 0 is centred at the top (rotation 0). Each segment fans
      // ±half-segment around its centre angle.
      const centre = -Math.PI / 2 + i * segmentAngle;
      const startAngle = centre - segmentAngle / 2;
      const endAngle = centre + segmentAngle / 2;

      const wedge = new Graphics();
      wedge.moveTo(0, 0);
      wedge.lineTo(radius * Math.cos(startAngle), radius * Math.sin(startAngle));
      wedge.arc(0, 0, radius, startAngle, endAngle, false);
      wedge.lineTo(0, 0);
      wedge.fill({ color: segment.tint, alpha: 0.95 });
      wedge.stroke({ color: 0xffffff, alpha: 0.18, width: 2 });
      this.wheel.addChild(wedge);

      // Label: place at 65% of the radius along the segment's centre angle.
      const labelDistance = radius * 0.62;
      const label = new Text({
        text: segment.label,
        style: {
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: Math.max(14, Math.floor(radius * 0.13)),
          fontWeight: '800',
          fill: 0xffffff,
          stroke: { color: 0x000000, width: 3, alpha: 0.55 },
          align: 'center',
        },
      });
      label.anchor.set(0.5);
      label.x = labelDistance * Math.cos(centre);
      label.y = labelDistance * Math.sin(centre);
      // Rotate the label so its baseline aligns radially outward.
      label.rotation = centre + Math.PI / 2;
      this.wheel.addChild(label);
    }

    // Hub: centre cap.
    const accent = this.config.chrome?.accentColor ?? 0xff6bd6;
    const frameFill = this.config.chrome?.frameFillColor ?? 0x1a0830;
    const hub = new Graphics();
    hub
      .circle(0, 0, Math.max(20, radius * 0.12))
      .fill({ color: frameFill, alpha: 0.9 })
      .stroke({ color: accent, alpha: 0.8, width: 3 });
    this.wheel.addChild(hub);
  }

  private drawPointer(): void {
    const radius = this.config.radius;
    const pointerFill = this.config.chrome?.pointerFillColor ?? 0xfff59d;
    const pointerStroke = this.config.chrome?.frameFillColor ?? 0x1a0830;
    // Pointer: triangle at the top edge pointing inward.
    this.pointer
      .moveTo(0, -radius - 8)
      .lineTo(-14, -radius + 18)
      .lineTo(14, -radius + 18)
      .lineTo(0, -radius - 8)
      .fill({ color: pointerFill })
      .stroke({ color: pointerStroke, width: 3 });
  }

  /** Reduce a rotation to [0, 2π). */
  private normaliseRotation(rotation: number): number {
    const twoPi = Math.PI * 2;
    return ((rotation % twoPi) + twoPi) % twoPi;
  }

  // Currently unused but kept for future external readers (e.g. live preview
  // of the segment that's currently under the pointer during free-spin).
  static segmentUnderPointer(rotation: number, segments: readonly WheelSegment[]): number {
    const segmentCount = segments.length;
    const segmentAngle = (Math.PI * 2) / segmentCount;
    const twoPi = Math.PI * 2;
    const normalised = ((-rotation % twoPi) + twoPi) % twoPi;
    return Math.floor((normalised + segmentAngle / 2) / segmentAngle) % segmentCount;
  }
}
